import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useResetPassword } from "~/api/authentication/authentication";
import { useGetPasswordRules } from "~/api/users/users";
import SmartPasswordInput from "~/components/smart-password-input";
import { Button } from "~/components/ui/button";
import { getStoredForgotPasswordEmail, ROUTES } from "~/helpers";
import { trackEvent } from "~/utils/analytics";

enum ResetPasswordFormState {
  CREATE_PASSWORD = "CREATE_PASSWORD",
  SUCCESS = "SUCCESS",
}

export const ResetPasswordForm = () => {
  const [formState, setFormState] = useState<ResetPasswordFormState>(
    ResetPasswordFormState.CREATE_PASSWORD
  );
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const code = searchParams.get("resetCode");

  const { mutate: resetPassword, isPending, isError } = useResetPassword();
  const { data: passwordRules, isLoading: isLoadingPasswordRules } =
    useGetPasswordRules();

  useEffect(() => {
    if (formState === ResetPasswordFormState.SUCCESS) {
      trackEvent({ eventName: "app/reset_password/success" });
    }
  }, [formState]);

  if (!code) {
    return <div>No reset code found</div>;
  }

  const handleSubmit = async (password: string) => {
    trackEvent({ eventName: "app/reset_password/save_password.button/submit" });

    const email = getStoredForgotPasswordEmail() || "";

    resetPassword(
      {
        data: {
          email,
          code,
          newPassword: password,
        },
      },
      {
        onSuccess: () => {
          setFormState(ResetPasswordFormState.SUCCESS);
        },
      }
    );
  };

  const navigateToLogin = () => {
    trackEvent({ eventName: "app/reset_password/log_in.button/click" });
    navigate(ROUTES.LOGIN);
  };

  return (
    <div className="mx-auto max-w-lg mt-10">
      {isLoadingPasswordRules ? (
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          {formState === ResetPasswordFormState.CREATE_PASSWORD && (
            <div className="flex flex-col gap-6 w-full">
              <h3 className="text-2xl font-semibold text-center text-base-foreground">
                Reset Password
              </h3>
              <div className="flex flex-col gap-4">
                <SmartPasswordInput
                  onPasswordSubmit={handleSubmit}
                  activeRequirements={passwordRules?.rules || []}
                  isPending={isPending}
                />
                <span className="text-sm text-muted-foreground font-medium text-center">
                  or
                </span>
                <Button
                  variant="secondary"
                  onClick={navigateToLogin}
                  className="w-full"
                >
                  Log In
                </Button>
              </div>
            </div>
          )}
          {formState === ResetPasswordFormState.SUCCESS && (
            <div className="flex flex-col gap-6 w-full">
              <div className="flex flex-col gap-4">
                <h3 className="text-2xl font-semibold text-center text-base-foreground">
                  Success
                </h3>
                <span className="text-sm leading-tight text-muted-foreground text-center">
                  Your password has been updated! You can now log in.
                </span>
              </div>
              <Button
                variant="secondary"
                className="w-full"
                onClick={navigateToLogin}
              >
                Log In
              </Button>
            </div>
          )}
          {isError && (
            <p className="mt-4 text-red-600 text-sm text-center">
              Failed to reset password. Please try again.
            </p>
          )}
        </>
      )}
    </div>
  );
};
